import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Building2, GraduationCap, ShieldCheck, Mail,
  TrendingUp, Network, ArrowRight, Printer, Stethoscope,
  Lock, UserCheck, AlertTriangle, Award,
} from 'lucide-react';

// HOSPITAL SPONSORSHIP — single-page brochure.
// Designed to fit a single A4 / one screen and be printable to PDF.
//
// FOUNDING PRINCIPLE (locked in chat log Apr 2026, never to be quietly drifted away from):
//     The doctor remains supreme. The hospital sponsorship grants the hospital exactly
//     one default right — to have its name listed as a verified training credential on a
//     consenting doctor's DocSite. Everything else (alumni hub inclusion, alma-mater
//     referrals, infrastructure usage, marketing material participation) is per-doctor,
//     per-instance, opt-in consent. Sponsorship creates ZERO obligation on the doctor
//     toward the hospital regarding clinical routing of patients.
//
// PRICING (founder-authorised): Rs. 25,000 / month + GST, flat — irrespective of headcount.
// Individual doctors continue to pay their own Gold/Premium/Turbo subscriptions at
// standard rates; the hospital fee does NOT subsidise individual subscriptions.

const PAIN_POINTS = [
  {
    icon: TrendingUp,
    headline: '5–10 years of training, then digitally invisible.',
    body: 'A hospital invests Rs. 50 L – 2 Cr training each resident. They walk out into independent practice and disappear from Google. Their first 3 years are spent re-earning visibility one patient at a time.',
  },
  {
    icon: Network,
    headline: 'Aggregators capture them inside 6 months.',
    body: 'Pay-per-listing platforms move faster than alumni networks. Once a fresh consultant is locked in, the doctor–hospital reputational bond starts fragmenting. The graduate\'s public surface stops carrying the hospital\'s name.',
  },
  {
    icon: Building2,
    headline: 'Your alumni network has no digital expression.',
    body: 'A hospital\'s reputation engine is its graduates. Today that reputation lives in WhatsApp groups and personal memory — not in indexed, verifiable, institution-branded surfaces a patient can find on Google when they search for one of your alumni.',
  },
];

const DOCTOR_RIGHTS = [
  'The doctor decides whether to display the hospital\'s "Trained at" badge on their DocSite. They can add it, remove it, or never enable it — at any time, with no notice and no penalty.',
  'Inclusion in the hospital\'s alumni hub directory is per-doctor opt-in. A doctor who does not consent is not listed. A doctor who later changes their mind is removed within 7 days.',
  'There is no automatic referral routing of any kind. When an alumnus encounters a case that needs tertiary infrastructure, the choice of hospital — alma mater or any other — remains the doctor\'s clinical decision, taken in the patient\'s interest.',
  'The doctor\'s clinical judgment is supreme. Sponsorship creates no contractual or reputational obligation toward the hospital regarding patient referrals, infrastructure usage, or marketing participation.',
  'On exit from the platform, or on revocation of sponsorship participation, the doctor takes their professional content, patient data, and SEO equity with them, in line with DocSite Terms § 6A.',
];

const HOSPITAL_GETS = [
  {
    icon: Award,
    title: 'Verified "Trained at" credential on consenting alumni DocSites',
    body: 'Each consenting graduate\'s DocSite carries "Trained at — [Hospital Name]" as a verified, schema-marked training credential. Indexed by Google. Visible at the moment a patient is choosing a doctor. Display is per-doctor opt-in and revocable.',
  },
  {
    icon: GraduationCap,
    title: 'Institution-branded alumni hub',
    body: 'A directory at /alumni/<your-hospital-slug> listing every consenting alumnus by specialty and city. SEO-indexed, owned by the institution, exportable as a print directory. A doctor can opt out at any time and is delisted within 7 days.',
  },
  {
    icon: Stethoscope,
    title: 'Optional alma-mater suggestion in the doctor\'s referral tooling',
    body: 'For doctors who choose to enable it, alma-mater hospitals appear as one option among many in their internal referral suggestion list — never as a default, never as an automatic route. The doctor\'s clinical decision remains the only mechanism that sends a patient anywhere.',
  },
  {
    icon: ShieldCheck,
    title: 'NMC + DPDPA training resources for sponsored cohorts',
    body: 'A 4-session compliance curriculum (patient communication, content authoring inside NMC ad rules, DPDPA-compliant data handling, audit-logged publishing) made available to every doctor the hospital chooses to onboard.',
  },
  {
    icon: Lock,
    title: 'Continuity for the institutional brand',
    body: 'A sponsored doctor who relocates retains their DocSite slug, content history, and SEO equity — and continues to display the hospital\'s training credential as long as they consent to it. The institutional brand asset compounds with every alumnus, every year.',
  },
];

export default function HospitalSponsorshipPage() {
  const subject = encodeURIComponent('Hospital Sponsorship — DocSite Pilot');
  const mailto = `mailto:contact@docsite.care?subject=${subject}`;

  return (
    <div className="min-h-screen bg-white print:bg-white">
      <Helmet>
        <title>Sponsor Your Graduates' First 3 Years of Practice | DocSite for Hospitals</title>
        <meta
          name="description"
          content="A 1-pager for hospitals and medical colleges: how to officially sponsor your MBBS, MS, and post-graduate alumni onto DocSite. The doctor remains supreme — sponsorship gives the hospital one default right (a verified 'Trained at' credential on consenting graduate profiles); everything else is per-doctor opt-in. Rs. 25,000/month + GST, flat."
        />
        <link rel="canonical" href="https://docsite.care/for/hospitals" />
      </Helmet>

      <article className="mx-auto max-w-[920px] px-6 md:px-10 py-10 md:py-14 print:py-6 print:px-8" data-testid="hospital-brochure">
        {/* Header band */}
        <header className="border-b border-slate-200 pb-6 mb-8 flex items-start justify-between gap-6">
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-sky-700 inline-flex items-center gap-1.5 mb-2">
              <Building2 size={11} /> For Hospitals &amp; Medical Colleges
            </p>
            <h1
              className="font-medium text-slate-900 tracking-tight leading-[1.05]"
              style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.75rem, 2.6vw, 2.85rem)' }}
              data-testid="hospital-brochure-h1"
            >
              Sponsor your graduates'<br className="hidden md:block" /> first 3 years of practice.
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.1rem)' }}>
              Your residents, registrars, and post-graduates spend 5–10 years training at your hospital. Their first 3 years in
              independent practice — when they're most invisible, most vulnerable to aggregator predation, and most likely to
              drift away from your alumni network — are now an <strong className="text-slate-900">institutional credential</strong>,
              not a leak.
            </p>
          </div>
          <div className="hidden md:flex flex-col items-end gap-2 print:hidden">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 text-xs hover:border-sky-400 hover:text-sky-700 transition-colors"
              data-testid="hospital-brochure-print"
            >
              <Printer size={13} /> Print as PDF
            </button>
            <p className="text-[10px] text-slate-400">DocSite · A ChainX Tech Labs product</p>
          </div>
        </header>

        {/* FOUNDING PRINCIPLE — must be the most visible thing on the page after the H1 */}
        <section className="mb-8 rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50/70 via-white to-sky-50/40 p-5 md:p-6" data-testid="hospital-brochure-principle">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 inline-flex items-center gap-1.5 mb-2">
            <UserCheck size={11} /> Founding principle — non-negotiable
          </p>
          <h2 className="font-medium text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(1.15rem, 1.5vw, 1.4rem)' }}>
            The doctor remains supreme. The sponsorship grants the hospital exactly one default right — to be listed as a verified training credential on a consenting graduate's DocSite. Everything else is the doctor's opt-in consent.
          </h2>
          <p className="text-xs text-slate-600 mt-3 leading-relaxed">
            DocSite is, and will always remain, a specialist-first platform. A patient's clinical journey begins with the doctor — and the doctor decides which hospital infrastructure best serves that patient, in that moment, for that case. A sponsorship cheque cannot, and will not, override that decision.
          </p>
        </section>

        {/* Section 1 — The institutional problem */}
        <section className="mb-8" data-testid="hospital-brochure-problem">
          <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-rose-700 mb-3">The institutional problem</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {PAIN_POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="rounded-xl border border-slate-200 p-4 bg-white">
                  <Icon size={18} className="text-rose-600 mb-2" />
                  <h3 className="text-sm font-semibold text-slate-900 mb-1.5 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {p.headline}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 2 — The doctor's rights, listed first (deliberate ordering) */}
        <section className="mb-8" data-testid="hospital-brochure-doctor-rights">
          <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-sky-700 mb-3">The doctor's rights — listed first, by design</h2>
          <ul className="space-y-2.5 rounded-xl border border-sky-200 bg-sky-50/30 p-5">
            {DOCTOR_RIGHTS.map((r, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-700 leading-relaxed" data-testid={`hospital-doctor-right-${i}`}>
                <UserCheck size={15} className="text-sky-700 flex-shrink-0 mt-0.5" />
                <span><span className="font-bold text-slate-900">{i + 1}.</span> {r}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Section 3 — What the hospital gets */}
        <section className="mb-8" data-testid="hospital-brochure-deliverables">
          <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700 mb-3">What the hospital gets</h2>
          <ul className="space-y-3">
            {HOSPITAL_GETS.map((d, i) => {
              const Icon = d.icon;
              return (
                <li key={i} className="flex items-start gap-3.5 rounded-xl border border-slate-200 p-4 bg-white" data-testid={`hospital-deliverable-${i}`}>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 flex-shrink-0">
                    <Icon size={16} className="text-emerald-700" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{d.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed mt-1">{d.body}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Section 4 — What this is NOT (sets expectations clearly) */}
        <section className="mb-8 rounded-xl border border-amber-200 bg-amber-50/40 p-5" data-testid="hospital-brochure-not">
          <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-amber-700 mb-3 inline-flex items-center gap-1.5">
            <AlertTriangle size={11} /> What this is not
          </h2>
          <ul className="text-sm text-slate-700 space-y-1.5 leading-relaxed">
            <li>• Not a referral contract — the hospital cannot oblige any sponsored doctor to refer patients to it.</li>
            <li>• Not a non-compete — sponsored doctors remain entirely free to practise wherever they choose, with whichever hospital partners they choose.</li>
            <li>• Not a marketing pipeline that overrides clinical judgment — the doctor's clinical decision is the only mechanism that routes a patient to any hospital, including the alma mater.</li>
            <li>• Not a kickback or commission arrangement — DocSite charges 0% commission on patient bookings on every tier, for every doctor, sponsored or not.</li>
          </ul>
        </section>

        {/* Section 5 — Commercial */}
        <section className="mb-8 rounded-2xl border-2 border-sky-200 bg-gradient-to-br from-sky-50/60 via-white to-emerald-50/40 p-6" data-testid="hospital-brochure-commercial">
          <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-sky-700 mb-3">Commercial structure</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Hospital pays</p>
              <p className="text-3xl md:text-4xl font-medium text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Rs. 25,000<span className="text-base font-normal text-slate-500"> / month + GST</span>
              </p>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                Flat platform support fee. Sponsor 50, 200, or 500 alumni — the fee does not change with headcount. Covers the
                "Trained at" verified credential, alumni hub, opt-in alma-mater suggestion in alumni doctors' referral tooling,
                NMC + DPDPA training resources, and a dedicated DocSite institutional success contact.
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Each sponsored doctor pays</p>
              <p className="text-3xl md:text-4xl font-medium text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Their own subscription
              </p>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                Standard DocSite pricing applies — Gold (free for the first 200 doctors on the platform, Rs. 99/month thereafter),
                Coffee Premium (Rs. 299/month), or TurboCharge (Rs. 999/month). The hospital fee <strong>does not</strong> subsidise
                individual subscriptions. See <Link to="/brochure" className="text-sky-700 underline decoration-dotted">/brochure</Link> for current plans.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6 — Pilot CTA */}
        <section className="mb-2" data-testid="hospital-brochure-cta">
          <h2 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-700 mb-3">How a pilot starts</h2>
          <ol className="text-sm text-slate-700 space-y-2 leading-relaxed list-decimal list-inside">
            <li>Email <a href={mailto} className="text-sky-700 underline decoration-dotted font-medium">contact@docsite.care</a> from your registrar's or HoD's address with the institution's name and approximate alumni headcount.</li>
            <li>30-minute scoping call within 7 days. We co-design the alumni-hub URL structure, the verified-credential wording, and the doctor-consent flow with your team.</li>
            <li>Pilot goes live within 30 days for the first cohort of consenting alumni. Quarterly review of alumni hub traffic, sponsored-doctor publishing activity, and credential adoption.</li>
          </ol>

          <div className="flex flex-wrap items-center gap-3 mt-6 print:hidden">
            <a
              href={mailto}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
              data-testid="hospital-brochure-email-cta"
            >
              <Mail size={14} /> Email us
            </a>
            <Link
              to="/dr/dr-aarav-mehta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-emerald-300 text-emerald-700 text-sm font-semibold hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
              data-testid="hospital-brochure-sample-cta"
            >
              See a live alumni DocSite <ArrowRight size={14} />
            </Link>
            <Link
              to="/brochure"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:border-sky-300 hover:text-sky-700 transition-colors"
            >
              See doctor pricing
            </Link>
          </div>
        </section>

        <footer className="border-t border-slate-200 pt-4 mt-8 text-[10px] text-slate-400 leading-relaxed">
          DocSite is a SaaS platform operated by ChainX Tech Labs. Hospital sponsorship terms are indicative and finalised in a
          per-institution Memorandum of Understanding which expressly preserves the doctor-supremacy provisions of this page.
          Doctor subscription pricing displayed at the time of registration is governed by{' '}
          <Link to="/terms/doctor" className="text-slate-500 underline decoration-dotted">DocSite Terms &amp; Conditions § 6 / § 6A</Link>{' '}
          (Free Plan Continuity, Voluntary Exit &amp; Data Portability). Indian Contract Act, 1872. Mumbai jurisdiction.
        </footer>
      </article>

      {/* Print-only styles to keep this single-page when printed */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          a { color: #0c4a6e; text-decoration: underline; }
          [data-testid="hospital-brochure"] { padding: 0 !important; max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}
